/**
 * Phase 5C — Scheduling Data Foundation (minimum viable input UI).
 * Phase 5E + 5B.8 — Schedules tab adds review/edit/revalidate/publish
 * workflow + PDF/XLSX roster downloads. Additive only.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/contexts/AuthContext";
import SchedulingAiPanel from "@/components/dashboard/SchedulingAiPanel";
import StorageUsageBadge from "@/components/dashboard/StorageUsageBadge";
import {
  CalendarClock, UserCog, Target, Clock, LayoutGrid, SlidersHorizontal,
  Trash2, Plus, Loader2, FileText, FileSpreadsheet, Play, ShieldCheck,
  Send, AlertTriangle, AlertCircle, Info, ChevronDown, ChevronRight,
  ListChecks, Pencil, X, Check, Lock, RefreshCw,
} from "lucide-react";

const TABS = [
  { id: "schedules",   label: "Schedules",       icon: ListChecks },
  { id: "templates",   label: "Shift templates", icon: LayoutGrid },
  { id: "skills",      label: "Skills",          icon: Target },
  { id: "empskills",   label: "Employee skills", icon: UserCog },
  { id: "availability",label: "Availability",    icon: Clock },
  { id: "demand",      label: "Staffing demand", icon: CalendarClock },
  { id: "policy",      label: "Policy",          icon: SlidersHorizontal },
];

export default function SchedulingModule() {
  const [tab, setTab] = useState("schedules");
  return (
    <div className="space-y-6" data-testid="scheduling-module">
      <div>
        <h1 className="text-2xl font-semibold notranslate" translate="no">Scheduling</h1>
        <p className="text-[14px] text-[#666] mt-1">
          Plan, review and publish rosters your team can rely on. Draft schedules go through
          validation before they become the official roster.
        </p>
        <StorageUsageBadge className="mt-3" />
      </div>
      <div className="border-b border-[#eee] flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`scheduling-tab-${t.id}`}
            className={`px-3 py-2 text-[13px] font-medium border-b-2 transition-colors ${
              tab === t.id ? "border-[#202B60] text-[#202B60]" : "border-transparent text-[#666] hover:text-black"
            }`}
          >
            <t.icon size={13} className="inline-block mr-1" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "schedules"    && <SchedulesPanel />}
      {tab === "templates"    && <ShiftTemplatesPanel />}
      {tab === "skills"       && <SkillsPanel />}
      {tab === "empskills"    && <EmployeeSkillsPanel />}
      {tab === "availability" && <AvailabilityPanel />}
      {tab === "demand"       && <DemandPanel />}
      {tab === "policy"       && <PolicyPanel />}
      {/* Phase 5F — Scheduling AI panel. Self-gates on backend feature
          flag via /api/ai/scheduling/catalogue 503. Hidden otherwise. */}
      <SchedulingAiPanel onAfterMutation={() => { /* refresh handled per-tab */ }} />
    </div>
  );
}

function useFetch(url, key, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = async () => {
    setLoading(true);
    try { const r = await axios.get(url, { withCredentials: true }); setData(r.data?.[key] ?? r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, deps);
  return { data, loading, reload };
}

function ShiftTemplatesPanel() {
  const { data: rows, reload } = useFetch(`${API}/scheduling/shift-templates`, "templates");
  const { data: skills } = useFetch(`${API}/scheduling/skills`, "skills");
  const [form, setForm] = useState({ name: "", start_time: "08:00", end_time: "16:00", required_skills: [] });
  const add = async () => {
    try { await axios.post(`${API}/scheduling/shift-templates`, form, { withCredentials: true }); toast.success("Template created"); reload(); setForm({ name: "", start_time: "08:00", end_time: "16:00", required_skills: [] }); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const del = async (id) => { await axios.delete(`${API}/scheduling/shift-templates/${id}`, { withCredentials: true }); reload(); };
  return (
    <div className="space-y-4" data-testid="scheduling-templates-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <input placeholder="Name" value={form.name} onChange={(e)=>setForm({ ...form, name:e.target.value })} className="zy-input text-[13px]" data-testid="scheduling-tmpl-name" />
        <input type="time" value={form.start_time} onChange={(e)=>setForm({ ...form, start_time:e.target.value })} className="zy-input text-[13px]" data-testid="scheduling-tmpl-start" />
        <input type="time" value={form.end_time} onChange={(e)=>setForm({ ...form, end_time:e.target.value })} className="zy-input text-[13px]" data-testid="scheduling-tmpl-end" />
        <select multiple value={form.required_skills} onChange={(e)=>setForm({ ...form, required_skills:[...e.target.selectedOptions].map(o=>o.value) })} className="zy-input text-[13px]" data-testid="scheduling-tmpl-skills">
          {(skills||[]).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button onClick={add} className="zy-btn-primary text-[13px]" data-testid="scheduling-tmpl-add-btn"><Plus size={13}/> Add</button>
      </div>
      <ul className="space-y-2">
        {(rows||[]).map((t)=>(
          <li key={t.id} className="flex items-center justify-between border border-[#eee] rounded-md px-3 py-2 text-[13px]" data-testid={`scheduling-tmpl-row-${t.id}`}>
            <div><b>{t.name}</b> · {t.start_time}–{t.end_time}{t.crosses_midnight ? " (next day)" : ""} · {t.required_skills?.length || 0} skill(s)</div>
            <button onClick={()=>del(t.id)} className="text-[#c00]" data-testid={`scheduling-tmpl-del-${t.id}`}><Trash2 size={14}/></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SkillsPanel() {
  const { data: rows, reload } = useFetch(`${API}/scheduling/skills`, "skills");
  const [form, setForm] = useState({ name: "", code: "" });
  const add = async () => {
    try { await axios.post(`${API}/scheduling/skills`, form, { withCredentials: true }); toast.success("Skill created"); reload(); setForm({ name: "", code: "" }); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const del = async (id) => { await axios.delete(`${API}/scheduling/skills/${id}`, { withCredentials: true }); reload(); };
  return (
    <div className="space-y-4" data-testid="scheduling-skills-panel">
      <div className="flex items-center gap-2">
        <input placeholder="Skill name" value={form.name} onChange={(e)=>setForm({ ...form, name:e.target.value })} className="zy-input text-[13px]" data-testid="scheduling-skill-name" />
        <input placeholder="code (slug)" value={form.code} onChange={(e)=>setForm({ ...form, code:e.target.value })} className="zy-input text-[13px]" data-testid="scheduling-skill-code" />
        <button onClick={add} className="zy-btn-primary text-[13px]" data-testid="scheduling-skill-add-btn"><Plus size={13}/> Add</button>
      </div>
      <ul className="space-y-2">
        {(rows||[]).map((s)=>(
          <li key={s.id} className="flex items-center justify-between border border-[#eee] rounded-md px-3 py-2 text-[13px]" data-testid={`scheduling-skill-row-${s.id}`}>
            <div><b>{s.name}</b> · <code className="text-[12px] text-[#555]">{s.code}</code></div>
            <button onClick={()=>del(s.id)} className="text-[#c00]"><Trash2 size={14}/></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmployeeSkillsPanel() {
  const { data: skills } = useFetch(`${API}/scheduling/skills`, "skills");
  const [emps, setEmps] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ employee_id: "", skill_id: "", level: 3 });
  const reloadAll = async () => {
    const [e, es] = await Promise.all([
      axios.get(`${API}/hr/employees`, { withCredentials: true }),
      axios.get(`${API}/scheduling/employee-skills`, { withCredentials: true }),
    ]);
    setEmps(e.data?.employees || []); setRows(es.data?.rows || []);
  };
  useEffect(() => { reloadAll(); }, []);
  const add = async () => {
    try { await axios.post(`${API}/scheduling/employee-skills`, form, { withCredentials: true }); toast.success("Granted"); reloadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  return (
    <div className="space-y-4" data-testid="scheduling-empskills-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={form.employee_id} onChange={(e)=>setForm({ ...form, employee_id:e.target.value })} className="zy-input text-[13px]" data-testid="scheduling-empskill-emp"><option value="">— employee —</option>{emps.map(e=><option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}</select>
        <select value={form.skill_id} onChange={(e)=>setForm({ ...form, skill_id:e.target.value })} className="zy-input text-[13px]" data-testid="scheduling-empskill-skill"><option value="">— skill —</option>{(skills||[]).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}</select>
        <select value={form.level} onChange={(e)=>setForm({ ...form, level:parseInt(e.target.value) })} className="zy-input text-[13px]" data-testid="scheduling-empskill-level">{[1,2,3,4,5].map(n=><option key={n} value={n}>Level {n}</option>)}</select>
        <button onClick={add} className="zy-btn-primary text-[13px]" data-testid="scheduling-empskill-add-btn"><Plus size={13}/> Grant</button>
      </div>
      <table className="w-full text-[13px] border border-[#eee] rounded-md"><thead className="bg-[#F4F6FB]"><tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Skill</th><th>Level</th><th></th></tr></thead>
        <tbody>{rows.map(r=><tr key={r.id} className="border-t border-[#eee]" data-testid={`scheduling-empskill-row-${r.id}`}>
          <td className="px-3 py-2">{emps.find(e=>e.id===r.employee_id)?.first_name||r.employee_id}</td>
          <td className="px-3 py-2">{(skills||[]).find(s=>s.id===r.skill_id)?.name||r.skill_id}</td>
          <td className="text-center">L{r.level}</td>
          <td className="text-right px-2"><button onClick={async()=>{await axios.delete(`${API}/scheduling/employee-skills/${r.id}`,{withCredentials:true});reloadAll();}} className="text-[#c00]"><Trash2 size={14}/></button></td>
        </tr>)}</tbody></table>
    </div>
  );
}

function AvailabilityPanel() {
  const [emps, setEmps] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ employee_id: "", kind: "recurring", weekday: 1, window_start: "09:00", window_end: "17:00", available: true });
  const reloadAll = async () => {
    const [e, av] = await Promise.all([
      axios.get(`${API}/hr/employees`, { withCredentials: true }),
      axios.get(`${API}/scheduling/availability`, { withCredentials: true }),
    ]);
    setEmps(e.data?.employees || []); setRows(av.data?.rows || []);
  };
  useEffect(() => { reloadAll(); }, []);
  const add = async () => {
    const payload = { ...form }; if (form.kind !== "exception") delete payload.date;
    try { await axios.post(`${API}/scheduling/availability`, payload, { withCredentials: true }); toast.success("Saved"); reloadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  return (
    <div className="space-y-4" data-testid="scheduling-availability-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={form.employee_id} onChange={(e)=>setForm({ ...form, employee_id:e.target.value })} className="zy-input text-[13px]" data-testid="scheduling-avail-emp"><option value="">— employee —</option>{emps.map(e=><option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}</select>
        <select value={form.kind} onChange={(e)=>setForm({ ...form, kind:e.target.value })} className="zy-input text-[13px]"><option value="recurring">Recurring</option><option value="exception">Exception</option></select>
        {form.kind === "recurring" ? (
          <select value={form.weekday} onChange={(e)=>setForm({ ...form, weekday:parseInt(e.target.value) })} className="zy-input text-[13px]">{["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d,i)=><option key={i} value={i}>{d}</option>)}</select>
        ) : (
          <input type="date" value={form.date || ""} onChange={(e)=>setForm({ ...form, date:e.target.value })} className="zy-input text-[13px]" />
        )}
        <input type="time" value={form.window_start} onChange={(e)=>setForm({ ...form, window_start:e.target.value })} className="zy-input text-[13px]" />
        <input type="time" value={form.window_end} onChange={(e)=>setForm({ ...form, window_end:e.target.value })} className="zy-input text-[13px]" />
        <label className="text-[13px] flex items-center gap-1"><input type="checkbox" checked={form.available} onChange={(e)=>setForm({ ...form, available:e.target.checked })} /> available</label>
        <button onClick={add} className="zy-btn-primary text-[13px]" data-testid="scheduling-avail-add-btn"><Plus size={13}/> Add</button>
      </div>
      <ul className="space-y-2">
        {rows.map(r=><li key={r.id} className="flex items-center justify-between border border-[#eee] rounded-md px-3 py-2 text-[13px]" data-testid={`scheduling-avail-row-${r.id}`}>
          <div>{emps.find(e=>e.id===r.employee_id)?.first_name||r.employee_id} · {r.kind==="recurring"?`weekday ${r.weekday}`:r.date} · {r.window_start}–{r.window_end} · {r.available?"available":"unavailable"}</div>
          <button onClick={async()=>{await axios.delete(`${API}/scheduling/availability/${r.id}`,{withCredentials:true});reloadAll();}} className="text-[#c00]"><Trash2 size={14}/></button>
        </li>)}
      </ul>
    </div>
  );
}

function DemandPanel() {
  const { data: rows, reload } = useFetch(`${API}/scheduling/staffing-demand`, "rows");
  const [form, setForm] = useState({ date: "", window_start: "09:00", window_end: "17:00", required_count: 1, source: "manual" });
  const add = async () => {
    try { await axios.post(`${API}/scheduling/staffing-demand`, form, { withCredentials: true }); toast.success("Demand added"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  return (
    <div className="space-y-4" data-testid="scheduling-demand-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <input type="date" value={form.date} onChange={(e)=>setForm({ ...form, date:e.target.value })} className="zy-input text-[13px]" data-testid="scheduling-demand-date" />
        <input type="time" value={form.window_start} onChange={(e)=>setForm({ ...form, window_start:e.target.value })} className="zy-input text-[13px]" />
        <input type="time" value={form.window_end} onChange={(e)=>setForm({ ...form, window_end:e.target.value })} className="zy-input text-[13px]" />
        <input type="number" min="0" value={form.required_count} onChange={(e)=>setForm({ ...form, required_count:parseInt(e.target.value||"0") })} className="zy-input text-[13px] w-20" data-testid="scheduling-demand-count" />
        <select value={form.source} onChange={(e)=>setForm({ ...form, source:e.target.value })} className="zy-input text-[13px]"><option value="manual">Manual (authoritative)</option><option value="work_order_prefill">Work-order prefill (advisory)</option></select>
        <button onClick={add} className="zy-btn-primary text-[13px]" data-testid="scheduling-demand-add-btn"><Plus size={13}/> Add</button>
      </div>
      <table className="w-full text-[13px] border border-[#eee] rounded-md"><thead className="bg-[#F4F6FB]"><tr><th className="px-3 py-2 text-left">Date</th><th>Window</th><th>Need</th><th>Source</th><th></th></tr></thead>
        <tbody>{(rows||[]).map(r=><tr key={r.id} className="border-t border-[#eee]" data-testid={`scheduling-demand-row-${r.id}`}>
          <td className="px-3 py-2">{r.date}</td><td className="text-center">{r.window_start}–{r.window_end}</td><td className="text-center">{r.required_count}</td>
          <td className="text-center"><span className={`text-[11px] uppercase font-bold px-2 py-0.5 rounded-full ${r.source==='manual'?'bg-[rgba(16,185,129,0.12)] text-[#059669]':'bg-[rgba(217,119,6,0.12)] text-[#b45309]'}`}>{r.source==='manual'?'manual':'advisory'}</span></td>
          <td className="text-right px-2"><button onClick={async()=>{await axios.delete(`${API}/scheduling/staffing-demand/${r.id}`,{withCredentials:true});reload();}} className="text-[#c00]"><Trash2 size={14}/></button></td>
        </tr>)}</tbody></table>
    </div>
  );
}

function PolicyPanel() {
  const { data: active, reload } = useFetch(`${API}/scheduling/policy`, null);
  const [form, setForm] = useState({ max_hours_per_day: "", min_rest_hours: "", max_consecutive_workdays: "" });
  useEffect(() => {
    if (active?.workspace_policy) {
      setForm({
        max_hours_per_day: active.workspace_policy.max_hours_per_day ?? "",
        min_rest_hours: active.workspace_policy.min_rest_hours ?? "",
        max_consecutive_workdays: active.workspace_policy.max_consecutive_workdays ?? "",
      });
    }
  }, [active]);
  const save = async () => {
    const wp = {};
    if (form.max_hours_per_day !== "") wp.max_hours_per_day = parseFloat(form.max_hours_per_day);
    if (form.min_rest_hours !== "") wp.min_rest_hours = parseFloat(form.min_rest_hours);
    if (form.max_consecutive_workdays !== "") wp.max_consecutive_workdays = parseInt(form.max_consecutive_workdays);
    try { await axios.post(`${API}/scheduling/policy`, { workspace_policy: wp }, { withCredentials: true }); toast.success(`Policy v${(active?.version||0)+1} activated`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  return (
    <div className="space-y-4" data-testid="scheduling-policy-panel">
      <div className="rounded-md bg-[#F4F6FB] p-3 text-[12px] text-[#555]">
        <b>Universal technical constraint:</b> overlap detection = <b>enabled</b> (non-overridable). No labour-law defaults are preset. Configure business rules below.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[13px]">
        <label>Max hours per day<input type="number" step="0.1" value={form.max_hours_per_day} onChange={(e)=>setForm({ ...form, max_hours_per_day:e.target.value })} className="zy-input mt-1 w-full" data-testid="scheduling-policy-max-hpd" placeholder="unset" /></label>
        <label>Min rest hours<input type="number" step="0.1" value={form.min_rest_hours} onChange={(e)=>setForm({ ...form, min_rest_hours:e.target.value })} className="zy-input mt-1 w-full" data-testid="scheduling-policy-min-rest" placeholder="unset" /></label>
        <label>Max consecutive workdays<input type="number" value={form.max_consecutive_workdays} onChange={(e)=>setForm({ ...form, max_consecutive_workdays:e.target.value })} className="zy-input mt-1 w-full" data-testid="scheduling-policy-max-cons" placeholder="unset" /></label>
      </div>
      <button onClick={save} className="zy-btn-primary text-[13px]" data-testid="scheduling-policy-save-btn">Save new policy version</button>
      <div className="text-[12px] text-[#888]">Active version: <b>{active?.version ?? 0}</b></div>
    </div>
  );
}


/* ============================================================================
 * Phase 5E + 5B.8 — Schedules workflow (customer-facing)
 * ========================================================================== */

/** Capabilities derived from active workspace membership level.
 *  Mirrors backend permission matrix in /app/backend/permissions_defaults.py.
 *   - Owner  (level 10)       → everything, including publish.
 *   - Director (level 8–9)    → publish allowed.
 *   - Manager (level 4–7)     → edit/revalidate/render; cannot publish.
 *   - Employee (level 2–3)    → read-only review.
 *   - Read-only (level ≤1)    → read-only.
 */
function useSchedulingCaps() {
  const { user, memberships, activeWorkspace } = useAuth() || {};
  return useMemo(() => {
    const isOwnWs = !!(user && activeWorkspace && user.id === activeWorkspace);
    const m = (memberships || []).find((x) => x.workspace_owner === activeWorkspace);
    const level = isOwnWs ? 10 : (m?.level ?? 0);
    const role = isOwnWs ? "Owner" : (m?.role ?? "");
    return {
      role,
      level,
      canRead: level >= 1,
      canEdit: level >= 4,     // write assignments
      canRender: level >= 4,   // PDF/XLSX
      canPublish: level >= 8,  // publish + draft preview exports
    };
  }, [user, memberships, activeWorkspace]);
}

function StatusBadge({ status, version }) {
  const cls = status === "published"
    ? "bg-[rgba(16,185,129,0.14)] text-[#059669]"
    : "bg-[rgba(99,102,241,0.14)] text-[#4f46e5]";
  const label = status === "published" ? `Published · v${version || 1}` : `Draft · v${version || 1}`;
  return (
    <span className={`inline-block text-[11px] uppercase font-bold px-2 py-0.5 rounded-full ${cls}`} data-testid="schedule-status-badge">
      {label}
    </span>
  );
}

async function downloadBlob(url, filename) {
  const resp = await axios.get(url, { withCredentials: true, responseType: "blob" });
  const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/octet-stream" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 500);
  return blob.size;
}

function SchedulesPanel() {
  const caps = useSchedulingCaps();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", period_start: "", period_end: "" });

  const reload = async (keepSelected = true) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/scheduling/schedules`, { withCredentials: true });
      const rows = r.data?.schedules || [];
      setList(rows);
      if (!keepSelected || !rows.find((s) => s.id === selectedId)) {
        setSelectedId(rows[0]?.id || null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(false); /* eslint-disable-next-line */ }, []);

  // Phase 5E — allow children to request selection of a newly-created
  // correction draft without lifting the whole flow.
  useEffect(() => {
    const h = async (e) => {
      await reload(true);
      if (e.detail) setSelectedId(e.detail);
    };
    window.addEventListener("zy-select-schedule", h);
    return () => window.removeEventListener("zy-select-schedule", h);
    // eslint-disable-next-line
  }, []);

  const createSchedule = async () => {
    if (!form.name || !form.period_start || !form.period_end) {
      toast.error("Give the schedule a name and a date range.");
      return;
    }
    try {
      const r = await axios.post(`${API}/scheduling/schedules`, form, { withCredentials: true });
      toast.success("Draft schedule created");
      setCreating(false);
      setForm({ name: "", period_start: "", period_end: "" });
      await reload(false);
      setSelectedId(r.data?.id || null);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4" data-testid="scheduling-schedules-panel">
      <aside className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-[14px] font-semibold">Schedules</h2>
          {caps.canEdit && (
            <button
              onClick={() => setCreating((v) => !v)}
              className="text-[12px] text-[#202B60] font-medium hover:underline"
              data-testid="schedule-new-toggle"
            >
              {creating ? "Cancel" : "+ New"}
            </button>
          )}
        </div>
        {creating && (
          <div className="border border-[#eee] rounded-md p-2 space-y-2 bg-white" data-testid="schedule-new-form">
            <input placeholder="Schedule name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="zy-input text-[13px] w-full" data-testid="schedule-new-name" />
            <div className="flex gap-2">
              <input type="date" value={form.period_start}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                className="zy-input text-[13px] w-full" data-testid="schedule-new-start" />
              <input type="date" value={form.period_end}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                className="zy-input text-[13px] w-full" data-testid="schedule-new-end" />
            </div>
            <button onClick={createSchedule}
              className="zy-btn-primary text-[13px] w-full"
              data-testid="schedule-new-create-btn">
              <Plus size={13} /> Create draft
            </button>
          </div>
        )}
        {loading ? (
          <div className="text-[13px] text-[#666] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : list.length === 0 ? (
          <div className="text-[13px] text-[#888] italic">No schedules yet.</div>
        ) : (
          <ul className="space-y-1" data-testid="schedules-list">
            {list.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => setSelectedId(s.id)}
                  data-testid={`schedule-row-${s.id}`}
                  className={`w-full text-left border rounded-md px-3 py-2 text-[13px] transition-colors ${
                    selectedId === s.id ? "border-[#202B60] bg-[rgba(32,43,96,0.04)]" : "border-[#eee] hover:border-[#ccc]"
                  }`}
                >
                  <div className="font-medium truncate">{s.name}</div>
                  <div className="text-[11px] text-[#666] mt-0.5">{s.period_start} → {s.period_end}</div>
                  <div className="mt-1"><StatusBadge status={s.status} version={s.version} /></div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>
      <section>
        {selectedId ? (
          <ScheduleDetail key={selectedId} scheduleId={selectedId} caps={caps} onChanged={() => reload(true)} />
        ) : (
          <div className="border border-dashed border-[#ddd] rounded-md p-8 text-center text-[13px] text-[#888]" data-testid="schedule-empty-state">
            Select or create a schedule to review assignments.
          </div>
        )}
      </section>
    </div>
  );
}

function ScheduleDetail({ scheduleId, caps, onChanged }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [skills, setSkills] = useState([]);
  const [busy, setBusy] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const [rev, emps, sks] = await Promise.all([
        axios.get(`${API}/scheduling/schedules/${scheduleId}/review`, { withCredentials: true }),
        axios.get(`${API}/hr/employees`, { withCredentials: true }).catch(() => ({ data: { employees: [] } })),
        axios.get(`${API}/scheduling/skills`, { withCredentials: true }).catch(() => ({ data: { skills: [] } })),
      ]);
      setReview(rev.data);
      setEmployees(emps.data?.employees || []);
      setSkills(sks.data?.skills || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [scheduleId]);

  const sched = review?.schedule;
  const isPublished = sched?.status === "published";
  const needsReval = !!review?.needs_revalidation;
  const errorCount = review?.error_count || 0;
  const warningCount = review?.warning_count || 0;
  const publishBlocked = !isPublished && (needsReval || errorCount > 0);

  // Published view uses frozen draft_snapshot; draft uses live.
  const assignments = isPublished
    ? (sched?.draft_snapshot?.assignments || [])
    : (review?.assignments || []);

  const empName = (eid) => {
    if (isPublished) {
      const a = assignments.find((x) => x.employee_id === eid);
      if (a?.employee_display_name) return a.employee_display_name;
    }
    const e = employees.find((x) => x.id === eid);
    return e ? `${e.first_name || ""} ${e.last_name || ""}`.trim() : "—";
  };
  const skillLabels = (a) => {
    if (isPublished && Array.isArray(a.skill_labels) && a.skill_labels.length) {
      return a.skill_labels.filter(Boolean);
    }
    return (a.skills_covered || []).map((sid) => {
      const s = skills.find((x) => x.id === sid);
      return s?.code || s?.name || "";
    }).filter(Boolean);
  };

  const runSolver = async () => {
    setBusy("solve");
    try {
      await axios.post(`${API}/scheduling/schedules/${scheduleId}/solve?replace=true`, {}, { withCredentials: true });
      toast.success("Solver run complete");
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  const runRevalidate = async () => {
    setBusy("revalidate");
    try {
      const r = await axios.post(`${API}/scheduling/schedules/${scheduleId}/revalidate`, {}, { withCredentials: true });
      if (r.data?.status === "clean") toast.success("Revalidation clean — publish is available.");
      else toast.warning(`Revalidation found ${r.data?.error_count || 0} blocking error(s).`);
      await reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || e.message);
    } finally { setBusy(""); }
  };

  const runPublish = async () => {
    setBusy("publish");
    try {
      await axios.post(`${API}/scheduling/schedules/${scheduleId}/publish`, {}, { withCredentials: true });
      toast.success("Schedule published.");
      await reload();
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "object" ? (d?.message || d?.code) : d;
      toast.error(msg || e.message);
    } finally { setBusy(""); }
  };

  const runCorrect = async () => {
    setBusy("correct");
    try {
      const r = await axios.post(`${API}/scheduling/schedules/${scheduleId}/correct`, {}, { withCredentials: true });
      toast.success("Correction draft created.");
      onChanged?.();
      // Jump to the new draft via parent list refresh — selection resets to top.
      // Caller already reloads and will show the latest in the list; the new one is at top because created_at sort.
      if (r.data?.id) {
        // Trigger a selection event through the parent by storing a hint on window; fallback: reload here.
        window.dispatchEvent(new CustomEvent("zy-select-schedule", { detail: r.data.id }));
      }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(""); }
  };

  const dlFile = async (kind, draft = false) => {
    setBusy(`dl-${kind}-${draft}`);
    try {
      const q = draft ? "?include_draft=true" : "";
      const ext = kind === "pdf" ? "pdf" : "xlsx";
      const safe = (sched?.name || "roster").replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 40);
      const fn = `hr_roster_${safe}_v${sched?.version || 1}${draft ? "_DRAFT" : ""}.${ext}`;
      const size = await downloadBlob(`${API}/scheduling/schedules/${scheduleId}/roster.${ext}${q}`, fn);
      if (!size) toast.error("Empty file returned.");
      else toast.success(`${kind.toUpperCase()} downloaded (${Math.round(size / 1024)} KB)`);
    } catch (e) {
      const d = e?.response?.data;
      // Blob responses need parsing for error payload
      if (d instanceof Blob) {
        try {
          const txt = await d.text();
          const parsed = JSON.parse(txt);
          toast.error(parsed?.detail?.message || parsed?.detail || "Download failed");
        } catch { toast.error("Download failed"); }
      } else {
        toast.error(d?.detail?.message || d?.detail || e.message);
      }
    } finally { setBusy(""); }
  };

  if (loading || !sched) {
    return (
      <div className="border border-[#eee] rounded-md p-6 text-[13px] text-[#666] flex items-center gap-2" data-testid="schedule-detail-loading">
        <Loader2 size={14} className="animate-spin" /> Loading schedule…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="schedule-detail">
      {/* Header */}
      <div className="border border-[#eee] rounded-md p-4 bg-white">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-[16px] font-semibold" data-testid="schedule-detail-name">{sched.name}</h2>
              <StatusBadge status={sched.status} version={sched.version} />
            </div>
            <div className="text-[12px] text-[#666] mt-1">
              {sched.period_start} → {sched.period_end}
              {isPublished && sched.published_by_display_name && (
                <> · Published by <b>{sched.published_by_display_name}</b></>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!isPublished && caps.canEdit && (
              <button onClick={runSolver} disabled={!!busy}
                className="text-[13px] inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#ddd] hover:border-[#202B60] hover:text-[#202B60] disabled:opacity-50"
                data-testid="schedule-solve-btn">
                {busy === "solve" ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
                Run solver
              </button>
            )}
            {!isPublished && caps.canEdit && (
              <button onClick={runRevalidate} disabled={!!busy}
                className="text-[13px] inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#ddd] hover:border-[#202B60] hover:text-[#202B60] disabled:opacity-50"
                data-testid="schedule-revalidate-btn">
                {busy === "revalidate" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                Revalidate
              </button>
            )}
            {!isPublished && caps.canPublish && (
              <button
                onClick={runPublish}
                disabled={publishBlocked || !!busy}
                title={
                  errorCount > 0 ? `Fix ${errorCount} blocking error(s) before publishing.`
                  : needsReval ? "Revalidate the schedule after your edits before publishing."
                  : "Publish this schedule as the official roster."
                }
                className={`text-[13px] inline-flex items-center gap-1 px-3 py-1.5 rounded-md font-medium ${
                  publishBlocked
                    ? "bg-[#eee] text-[#999] cursor-not-allowed"
                    : "bg-[#202B60] text-white hover:bg-[#182149]"
                }`}
                data-testid="schedule-publish-btn">
                {busy === "publish" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                Publish
              </button>
            )}
            {isPublished && caps.canEdit && (
              <button onClick={runCorrect} disabled={!!busy}
                className="text-[13px] inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#ddd] hover:border-[#202B60] hover:text-[#202B60] disabled:opacity-50"
                data-testid="schedule-correct-btn">
                {busy === "correct" ? <Loader2 size={13} className="animate-spin" /> : <Pencil size={13} />}
                Create correction
              </button>
            )}
            {isPublished && caps.canRender && (
              <>
                <button onClick={() => dlFile("pdf")} disabled={!!busy}
                  className="text-[13px] inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#ddd] hover:border-[#202B60] hover:text-[#202B60] disabled:opacity-50"
                  data-testid="schedule-download-pdf-btn">
                  <FileText size={13} /> PDF
                </button>
                <button onClick={() => dlFile("xlsx")} disabled={!!busy}
                  className="text-[13px] inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#ddd] hover:border-[#202B60] hover:text-[#202B60] disabled:opacity-50"
                  data-testid="schedule-download-xlsx-btn">
                  <FileSpreadsheet size={13} /> XLSX
                </button>
              </>
            )}
            {!isPublished && caps.canPublish && (
              <>
                <button onClick={() => dlFile("pdf", true)} disabled={!!busy}
                  className="text-[13px] inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-dashed border-[#ddd] hover:border-[#b45309] hover:text-[#b45309] disabled:opacity-50"
                  data-testid="schedule-preview-pdf-btn"
                  title="Download a watermarked DRAFT preview (not an official roster).">
                  <FileText size={13} /> Preview DRAFT PDF
                </button>
                <button onClick={() => dlFile("xlsx", true)} disabled={!!busy}
                  className="text-[13px] inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-dashed border-[#ddd] hover:border-[#b45309] hover:text-[#b45309] disabled:opacity-50"
                  data-testid="schedule-preview-xlsx-btn">
                  <FileSpreadsheet size={13} /> Preview DRAFT XLSX
                </button>
              </>
            )}
          </div>
        </div>

        {/* Status/gate banners */}
        {isPublished && (
          <div className="mt-3 flex items-start gap-2 text-[12px] text-[#059669] bg-[rgba(16,185,129,0.08)] border border-[rgba(16,185,129,0.25)] rounded-md px-3 py-2"
               data-testid="schedule-published-banner">
            <Lock size={14} className="mt-0.5" />
            <div>
              Published roster — immutable. To change anything, start a <b>correction</b>; this preserves the published version as history.
            </div>
          </div>
        )}
        {!isPublished && needsReval && errorCount === 0 && (
          <div className="mt-3 flex items-start gap-2 text-[12px] text-[#b45309] bg-[rgba(217,119,6,0.08)] border border-[rgba(217,119,6,0.25)] rounded-md px-3 py-2"
               data-testid="schedule-needs-revalidation-banner">
            <AlertTriangle size={14} className="mt-0.5" />
            <div>
              Manual edits are pending validation. Click <b>Revalidate</b> before this schedule can be published.
            </div>
          </div>
        )}
        {!isPublished && errorCount > 0 && (
          <div className="mt-3 flex items-start gap-2 text-[12px] text-[#b91c1c] bg-[rgba(220,38,38,0.08)] border border-[rgba(220,38,38,0.25)] rounded-md px-3 py-2"
               data-testid="schedule-errors-block-banner">
            <AlertCircle size={14} className="mt-0.5" />
            <div>
              {errorCount} blocking issue{errorCount === 1 ? "" : "s"} must be resolved before publishing. Warnings do not block publishing.
            </div>
          </div>
        )}
        {!isPublished && !needsReval && errorCount === 0 && warningCount > 0 && (
          <div className="mt-3 flex items-start gap-2 text-[12px] text-[#7c6500] bg-[rgba(234,179,8,0.08)] border border-[rgba(234,179,8,0.25)] rounded-md px-3 py-2"
               data-testid="schedule-warnings-notice-banner">
            <Info size={14} className="mt-0.5" />
            <div>
              {warningCount} warning{warningCount === 1 ? "" : "s"} present — review before publishing. Warnings don't block publication.
            </div>
          </div>
        )}
      </div>

      {/* Validation groups */}
      <ValidationGroups validations={review?.validations || {}} skills={skills} employees={employees} />

      {/* Assignments table */}
      <AssignmentsTable
        assignments={assignments}
        isPublished={isPublished}
        canEdit={caps.canEdit && !isPublished}
        empName={empName}
        skillLabels={skillLabels}
        onEdited={reload}
      />
    </div>
  );
}

function Group({ title, items, Icon, color, testid, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!items || items.length === 0) return null;
  return (
    <div className="border border-[#eee] rounded-md bg-white" data-testid={testid}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-[13px] font-medium"
        data-testid={`${testid}-toggle`}
      >
        <span className="inline-flex items-center gap-2" style={{ color }}>
          <Icon size={14} /> {title}
          <span className="text-[11px] bg-[#f4f4f4] text-[#555] px-1.5 py-0.5 rounded-full">{items.length}</span>
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <ul className="divide-y divide-[#f0f0f0]">
          {items.map((v, i) => (
            <li key={v.id || i} className="px-3 py-2 text-[12px] text-[#333]" data-testid={`${testid}-item-${i}`}>
              <div>{v.message || v.code || "Validation finding"}</div>
              {v.date && <div className="text-[11px] text-[#777] mt-0.5">Date: {v.date}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ValidationGroups({ validations }) {
  const errors = validations.errors || [];
  const warnings = validations.warnings || [];
  const info = validations.info || [];
  const advisory = validations.advisory || [];
  const anything = errors.length + warnings.length + info.length + advisory.length;
  if (anything === 0) {
    return (
      <div className="border border-[#eee] rounded-md bg-white px-3 py-3 text-[12px] text-[#059669] inline-flex items-center gap-2"
           data-testid="schedule-validations-empty">
        <ShieldCheck size={14} /> No validation findings on the current authoritative run.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="schedule-validations">
      <Group title="Errors — must fix before publishing" items={errors} Icon={AlertCircle}
        color="#b91c1c" testid="val-errors" defaultOpen />
      <Group title="Warnings — review before publishing" items={warnings} Icon={AlertTriangle}
        color="#b45309" testid="val-warnings" defaultOpen />
      <Group title="Info" items={info} Icon={Info} color="#202B60"
        testid="val-info" defaultOpen={false} />
      <Group title="Previous / advisory diagnostics" items={advisory} Icon={Info}
        color="#6b7280" testid="val-advisory" defaultOpen={false} />
    </div>
  );
}

function AssignmentsTable({ assignments, isPublished, canEdit, empName, skillLabels, onEdited }) {
  const [editing, setEditing] = useState(null); // {id, window_start, window_end}
  const [saving, setSaving] = useState(false);

  const startEdit = (a) => setEditing({ id: a.id, window_start: a.window_start || "", window_end: a.window_end || "" });
  const cancelEdit = () => setEditing(null);
  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/scheduling/schedule-assignments/${editing.id}`,
        { window_start: editing.window_start, window_end: editing.window_end },
        { withCredentials: true });
      toast.success("Assignment updated — remember to revalidate.");
      setEditing(null);
      onEdited?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  if (!assignments || assignments.length === 0) {
    return (
      <div className="border border-dashed border-[#ddd] rounded-md p-6 text-center text-[13px] text-[#888]"
           data-testid="schedule-assignments-empty">
        No assignments yet. {canEdit ? "Run the solver to generate a draft." : ""}
      </div>
    );
  }

  // group by date for readability
  const groups = {};
  for (const a of assignments) {
    const k = a.date || "—";
    (groups[k] = groups[k] || []).push(a);
  }
  const dates = Object.keys(groups).sort();

  return (
    <div className="border border-[#eee] rounded-md bg-white overflow-hidden" data-testid="schedule-assignments-table">
      <div className="px-3 py-2 border-b border-[#eee] text-[13px] font-semibold bg-[#F4F6FB]">
        Assignments ({assignments.length})
      </div>
      {dates.map((date) => (
        <div key={date} data-testid={`assignments-day-${date}`}>
          <div className="px-3 py-1.5 text-[12px] font-medium text-[#555] bg-[#fafafa]">{date}</div>
          <table className="w-full text-[13px]">
            <tbody>
              {groups[date].map((a) => {
                const isEditing = editing?.id === a.id;
                return (
                  <tr key={a.id} className="border-t border-[#f0f0f0]"
                      data-testid={`assignment-row-${a.id}`}>
                    <td className="px-3 py-2 w-[200px]">
                      <div className="font-medium" data-testid={`assignment-emp-${a.id}`}>{empName(a.employee_id)}</div>
                      <div className="text-[11px] text-[#777]">{skillLabels(a).join(" · ") || "—"}</div>
                    </td>
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <input type="time" value={editing.window_start}
                            onChange={(e) => setEditing({ ...editing, window_start: e.target.value })}
                            className="zy-input text-[12px]" data-testid={`assignment-edit-start-${a.id}`} />
                          <span>→</span>
                          <input type="time" value={editing.window_end}
                            onChange={(e) => setEditing({ ...editing, window_end: e.target.value })}
                            className="zy-input text-[12px]" data-testid={`assignment-edit-end-${a.id}`} />
                        </div>
                      ) : (
                        <span data-testid={`assignment-window-${a.id}`}>{a.window_start}–{a.window_end}</span>
                      )}
                      {a.edited_manually && !isEditing && (
                        <span className="ml-2 text-[11px] text-[#b45309] uppercase font-bold" data-testid={`assignment-edited-badge-${a.id}`}>
                          edited
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right w-[120px]">
                      {canEdit && (
                        isEditing ? (
                          <div className="inline-flex items-center gap-2">
                            <button onClick={saveEdit} disabled={saving}
                              className="text-[#059669] inline-flex items-center gap-1 text-[12px]"
                              data-testid={`assignment-save-${a.id}`}>
                              {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
                            </button>
                            <button onClick={cancelEdit} disabled={saving}
                              className="text-[#666] inline-flex items-center gap-1 text-[12px]"
                              data-testid={`assignment-cancel-${a.id}`}>
                              <X size={12} /> Cancel
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => startEdit(a)}
                            className="text-[#202B60] inline-flex items-center gap-1 text-[12px]"
                            data-testid={`assignment-edit-${a.id}`}>
                            <Pencil size={12} /> Edit
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
